import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../../components/TeamLogo';
import { LivePlayer, LiveTeam } from '../../services/LiveService';
import { fmtR3 } from '../MatchPage2/matchStats';
import pageStyles from '../MatchPage2/MatchPage2.module.css';
import styles from './Live.module.css';

interface LiveScoreboardProps {
    team: LiveTeam;
    players: LivePlayer[];
}

// One team's live board, in the MatchPage2 board markup:
// SPILLER | K/D/A | +/- | ADR | KAST | HS% | RATING 1.0 (HS% drops on phones).
const LiveScoreboard: React.FC<LiveScoreboardProps> = ({ team, players }) => {
    const { t } = useTranslation('pages');
    const { t: ts } = useTranslation('stats');

    // Best first, like the match page; kills break ties while ratings are still level early on.
    const rows = useMemo(
        () => [...players].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || b.kills - a.kills),
        [players],
    );

    return (
        <div className={pageStyles.board}>
            <div className={pageStyles.boardHeader}>
                <TeamLogo logo={team.logo} name={team.name} teamId={team.teamId} className={pageStyles.boardLogo} />
                {team.teamId
                    ? <Link to={`/team/${team.teamId}`} className={pageStyles.boardTeamName}>{team.name}</Link>
                    : <span className={pageStyles.boardTeamName}>{team.name}</span>}
                <span className={pageStyles.boardScore}>({team.score})</span>
            </div>
            <div className={`${pageStyles.boardCols} ${styles.liveCols}`}>
                <div>{t('match2.player')}</div>
                <div className={pageStyles.cellCenter}>{ts('kda')}</div>
                <div className={pageStyles.cellCenter}>{t('live.plusMinus')}</div>
                <div className={pageStyles.cellCenter}>{ts('adr')}</div>
                <div className={pageStyles.cellCenter}>{ts('kast')}</div>
                <div className={`${pageStyles.cellCenter} ${styles.hideSm}`}>{ts('hs')}</div>
                <div className={pageStyles.cellCenter}>{t('live.rating10')}</div>
            </div>
            {rows.map((p, i) => {
                const diff = p.kills - p.deaths;
                const name = (
                    <span className={pageStyles.playerCellName}>{p.name || p.steamId.slice(-6)}</span>
                );
                return (
                    <div key={p.steamId || i} className={`${pageStyles.boardRow} ${styles.liveRow} ${i % 2 === 1 ? pageStyles.boardRowAlt : ''}`}>
                        {p.steamId
                            ? <Link to={`/player/${p.steamId}`} className={pageStyles.playerCell}>{name}</Link>
                            : <div className={pageStyles.playerCell}>{name}</div>}
                        <div className={`${pageStyles.cellCenter} ${pageStyles.cellKd}`}>{p.kills}/{p.deaths}/{p.assists}</div>
                        <div className={`${pageStyles.cellCenter} ${diff > 0 ? pageStyles.good : diff < 0 ? pageStyles.bad : ''}`}>
                            {diff > 0 ? `+${diff}` : diff}
                        </div>
                        <div className={pageStyles.cellCenter}>{p.adr != null ? p.adr.toFixed(1) : '–'}</div>
                        <div className={pageStyles.cellCenter}>{p.kast != null ? `${Math.round(p.kast)}%` : '–'}</div>
                        <div className={`${pageStyles.cellCenter} ${styles.hideSm}`}>{p.hsPct != null ? `${Math.round(p.hsPct)}%` : '–'}</div>
                        <div className={`${pageStyles.cellCenter} ${pageStyles.r3Cell} ${p.rating != null ? (p.rating >= 1.0 ? pageStyles.good : pageStyles.bad) : ''}`}>
                            {fmtR3(p.rating)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default LiveScoreboard;
